import { useEffect, useRef } from 'react'
import { CheckOutlined } from '../../components/icons'
import { formatMoney } from '../cart/cart-money'
import { formatOrderRef } from './checkout-domain'
import type { PoBreakdownGroup } from './checkout-domain'

export interface OrderPlacedCelebrationProps {
  readonly orderId: string
  /** Called once, when the celebration is done and we should move on to the order.
   * Fired by the auto-advance timer, the Skip control, the "View order" link, or a
   * veil click — all navigate to the order (matching the prototype, which dismisses
   * the overlay and opens the order after ~3.2s; `Fictiv Platform.dc.html` L7186). */
  readonly onViewOrder: () => void
  /** UID-97 K1 — present after a split-PO batch placement
   * (`POST /orders/batch`'s `PlaceOrdersResult.poBreakdown`, ADR-0044 §1):
   * renders the server-derived per-order breakdown alongside an "N orders
   * placed" subtitle instead of the single-order one. Money here is read
   * straight off the server response — never client-recomputed. */
  readonly poBreakdown?: readonly PoBreakdownGroup[] | undefined
}

/**
 * Auto-advance delay — matches the prototype's `setTimeout(..., 3200)` that dismisses the
 * order-placed overlay and opens the order (`Fictiv Platform.dc.html` L7186). A single hold
 * for both motion paths so reduced-motion users get the same time to read before navigating;
 * only the VISUAL (full scene vs gentle fade) differs by preference.
 */
const AUTO_ADVANCE_MS = 3200

/**
 * The gear silhouette from the prototype's order-placed scene
 * (`Fictiv Platform.dc.html` L4303/L4308 — a Lucide "settings" gear, drawn in a
 * 0–24 box). Shared verbatim by the large brand gear and the small teal
 * counter-gear; the outer <g> transforms place/scale each instance.
 */
const GEAR_PATH =
  'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z'

/**
 * The order-placed celebration (UX spec §5, interactions.md §7) — a single full-screen veil +
 * card that plays the staged gears/ring/spark → checkmark → confetti sequence (~3.1s, `opa-*`
 * keyframes in checkout.css) and then **auto-navigates to the order** (design behavior — the
 * overlay dismisses itself and opens the order after ~3.2s; it does NOT resolve into a separate
 * persistent card on the checkout page). A visible LABELED "Skip animation" control and the
 * "View order" link both advance immediately; a veil click does too.
 *
 * `prefers-reduced-motion: reduce` swaps the large-motion scene for a short gentle opacity fade
 * of the same card content (§A2) — a different, simpler render path, never skipped outright —
 * while keeping the same {@link AUTO_ADVANCE_MS} hold before navigating.
 */
export function OrderPlacedCelebration({
  orderId,
  onViewOrder,
  poBreakdown,
}: OrderPlacedCelebrationProps) {
  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const cardRef = useRef<HTMLDivElement>(null)

  // Auto-advance to the order once the celebration has played (prototype L7186). Skip / View
  // order / veil-click call `onViewOrder` directly to advance sooner.
  useEffect(() => {
    const timer = window.setTimeout(onViewOrder, AUTO_ADVANCE_MS)
    return () => window.clearTimeout(timer)
  }, [onViewOrder])

  // §5.5 focus management: move focus into the DIALOG CARD (tabIndex=-1) on mount, not onto the
  // Skip button — programmatic focus on a button triggers its `:focus` ring (a stray blue
  // selector on the celebration), whereas focusing the non-interactive dialog container
  // announces the dialog to AT without painting a control-focus outline.
  useEffect(() => {
    cardRef.current?.focus()
  }, [])

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape-equivalent keyboard path is the always-present, always-focusable Skip button below; the veil click is a mouse convenience layer only (same pattern as Modal.tsx).
    <div
      className="opa-veil"
      onClick={(event) => {
        if (event.target === event.currentTarget) onViewOrder()
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: hand-rolled dialog shell shares the Modal.tsx focus-trap-free pattern (celebration is dismiss-anywhere, not a form); see Modal.tsx's own identical deviation note. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="opa-title"
        tabIndex={-1}
        ref={cardRef}
        className="opa-card"
      >
        <button type="button" className="opa-skip-btn" onClick={onViewOrder}>
          Skip animation
        </button>

        {!prefersReducedMotion && (
          <div className="opa-scene" aria-hidden="true">
            {/* Single SVG scene matching the prototype (Fictiv Platform.dc.html
             * §order-placed): a track ring + teal progress ring, a large brand
             * gear and a small teal counter-gear (real gear silhouettes, not
             * bordered circles), and one orange spark. */}
            <svg className="opa-scene-svg" viewBox="0 0 120 120" aria-hidden="true">
              <circle className="opa-ring-track" cx="60" cy="60" r="47" />
              <circle className="opa-ring" cx="60" cy="60" r="47" transform="rotate(-90 60 60)" />
              <g transform="translate(52 54) scale(1.15) translate(-12 -12)">
                <g className="opa-gear opa-gear-lg">
                  <path d={GEAR_PATH} />
                  <circle cx="12" cy="12" r="3" />
                </g>
              </g>
              <g transform="translate(74 76) scale(0.7) translate(-12 -12)">
                <g className="opa-gear opa-gear-sm">
                  <path d={GEAR_PATH} />
                  <circle cx="12" cy="12" r="3" />
                </g>
              </g>
              <g className="opa-spark">
                <path d="M60 22l1.6 4.4 4.4 1.6-4.4 1.6-1.6 4.4-1.6-4.4-4.4-1.6 4.4-1.6z" />
              </g>
            </svg>
            <span className="opa-badge">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path className="opa-check" d="M6 12.5l4 4 8-9" />
              </svg>
            </span>
            {Array.from({ length: 6 }, (_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed set of 6 purely decorative, never-reordered confetti pieces.
              <span key={index} className={`opa-confetti opa-confetti-${index + 1}`} />
            ))}
          </div>
        )}

        <div
          className={[
            'opa-resolved-content',
            prefersReducedMotion ? 'opa-gentle-fade-in' : 'opa-fade-in',
          ].join(' ')}
        >
          <span className="opa-static-check" aria-hidden="true">
            <CheckOutlined />
          </span>
          <h2 id="opa-title" className="opa-title">
            {poBreakdown && poBreakdown.length > 1
              ? `${poBreakdown.length} orders placed`
              : 'Order placed'}
          </h2>
          <p className="opa-subtitle">Order {formatOrderRef(orderId)}</p>
          {poBreakdown && poBreakdown.length > 1 && (
            <dl className="opa-po-breakdown">
              {poBreakdown.map((po) => (
                <div key={po.orderId} className="opa-po-breakdown-row">
                  <dt>
                    <b>{po.poLabel}</b> · {po.lineCount} {po.lineCount === 1 ? 'line' : 'lines'}
                  </dt>
                  <dd>{formatMoney(po.summary.amount, po.summary.currency)}</dd>
                </div>
              ))}
            </dl>
          )}
          {!prefersReducedMotion && (
            <div className="opa-progress-bar" aria-hidden="true">
              <div className="opa-progress-bar-fill" />
            </div>
          )}
          <a
            href={`/orders/${orderId}`}
            className="opa-view-order-link"
            onClick={(event) => {
              event.preventDefault()
              onViewOrder()
            }}
          >
            View order →
          </a>
        </div>
      </div>
    </div>
  )
}
